import { auth, ensureAppCheckReady, getAppCheckDebugState } from '../config/firebase.web';
import {
  signInWithPhoneNumber,
  RecaptchaVerifier,
  ConfirmationResult,
  User,
  signOut as firebaseSignOut,
  onAuthStateChanged as firebaseOnAuthStateChanged,
  onIdTokenChanged as firebaseOnIdTokenChanged,
} from 'firebase/auth';

export interface PhoneAuthResult {
  success: boolean;
  error?: string;
  verificationId?: string;
  confirmation?: ConfirmationResult;
}

export interface VerifyOtpResult {
  success: boolean;
  error?: string;
  user?: User;
}

let recaptchaVerifier: RecaptchaVerifier | null = null;
let lastOtpRequestTime: number = 0;
let sendOtpInFlight: Promise<PhoneAuthResult> | null = null;
const OTP_COOLDOWN_MS = 60000; // 60 seconds cooldown between requests

const RECAPTCHA_CONTAINER_ID = 'recaptcha-container';

const styleRecaptchaContainer = (el: HTMLElement) => {
  // Keep off-screen for invisible reCAPTCHA; still in DOM for Firebase
  el.style.position = 'fixed';
  el.style.bottom = '0';
  el.style.right = '0';
  el.style.zIndex = '9999';
};

/**
 * grecaptcha tracks widgets by DOM node identity in `___grecaptcha_cfg.clients`.
 * RecaptchaVerifier.clear() + innerHTML='' leave that identity in place, so the
 * next render() on the same node throws "reCAPTCHA has already been rendered".
 * Replacing the host node gives grecaptcha a fresh element.
 */
const replaceRecaptchaContainer = (): HTMLElement => {
  if (typeof document === 'undefined') {
    throw new Error('reCAPTCHA requires a browser environment.');
  }

  const next = document.createElement('div');
  next.id = RECAPTCHA_CONTAINER_ID;
  styleRecaptchaContainer(next);

  const existing = document.getElementById(RECAPTCHA_CONTAINER_ID);
  if (existing?.parentNode) {
    existing.parentNode.replaceChild(next, existing);
  } else {
    document.body.appendChild(next);
  }
  return next;
};

/**
 * Tear down any existing reCAPTCHA widget and its host node.
 * Tokens are single-use; a failed sendOTP leaves a dead verifier if we keep it.
 */
const clearRecaptcha = (): HTMLElement | null => {
  if (recaptchaVerifier) {
    try {
      recaptchaVerifier.clear();
    } catch {
      // Widget may already be disposed
    }
    recaptchaVerifier = null;
  }

  if (typeof document === 'undefined') {
    return null;
  }

  return replaceRecaptchaContainer();
};

const createVerifier = (container: HTMLElement): RecaptchaVerifier => {
  return new RecaptchaVerifier(auth, container, {
    size: 'invisible',
    callback: () => {
      // Token ready; signInWithPhoneNumber consumes it
    },
    'expired-callback': () => {
      // Do not tear down the DOM here — sendOTP may still be in flight.
      console.warn('reCAPTCHA expired — will recreate on next attempt');
      recaptchaVerifier = null;
    },
    'error-callback': (err: unknown) => {
      console.error('reCAPTCHA error:', err);
      recaptchaVerifier = null;
    },
  });
};

const isAlreadyRenderedError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return message.toLowerCase().includes('already been rendered');
};

/**
 * Phone Auth RecaptchaVerifier (v2 invisible) is independent of App Check's
 * ReCaptchaEnterpriseProvider. Reuse the live instance; only mint a new one
 * after clearRecaptcha() (success, failure, or expiry).
 */
const initializeRecaptcha = async (): Promise<RecaptchaVerifier> => {
  if (recaptchaVerifier) {
    return recaptchaVerifier;
  }

  const container = clearRecaptcha();
  if (!container) {
    throw new Error('reCAPTCHA requires a browser environment.');
  }

  recaptchaVerifier = createVerifier(container);

  try {
    await recaptchaVerifier.render();
  } catch (error) {
    if (!isAlreadyRenderedError(error)) {
      throw error;
    }

    // Leftover widget from HMR / a previous session that clear() missed.
    try {
      recaptchaVerifier.clear();
    } catch {
      // already disposed
    }
    recaptchaVerifier = null;
    const fresh = replaceRecaptchaContainer();
    recaptchaVerifier = createVerifier(fresh);
    await recaptchaVerifier.render();
  }

  return recaptchaVerifier;
};

/**
 * Send OTP to phone number (Web)
 */
const sendOTPInternal = async (phoneNumber: string): Promise<PhoneAuthResult> => {
  try {
    if (!phoneNumber.startsWith('+')) {
      return {
        success: false,
        error: 'Phone number must include country code (e.g., +1234567890)',
      };
    }

    // Check cooldown period to prevent rate limiting
    const now = Date.now();
    const timeSinceLastRequest = now - lastOtpRequestTime;

    if (timeSinceLastRequest < OTP_COOLDOWN_MS && lastOtpRequestTime > 0) {
      const remainingSeconds = Math.ceil((OTP_COOLDOWN_MS - timeSinceLastRequest) / 1000);
      return {
        success: false,
        error: `Please wait ${remainingSeconds} seconds before requesting another OTP.`,
      };
    }

    await ensureAppCheckReady();

    const appVerifier = await initializeRecaptcha();

    const confirmation = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);

    lastOtpRequestTime = now;

    // Token is consumed — drop verifier so the next resend builds a new one
    clearRecaptcha();

    return {
      success: true,
      verificationId: confirmation.verificationId,
      confirmation,
    };
  } catch (error: any) {
    const location =
      typeof window !== 'undefined'
        ? { hostname: window.location.hostname, origin: window.location.origin }
        : { hostname: undefined, origin: undefined };
    console.error('[PhoneAuth Debug]', {
      code: error?.code,
      message: error?.message,
      hostname: location.hostname,
      origin: location.origin,
      ...getAppCheckDebugState(),
    });
    // Always reset reCAPTCHA after failure so the next try gets a fresh token
    clearRecaptcha();

    let errorMessage = 'Failed to send OTP. Please try again.';

    if (error.code === 'auth/invalid-phone-number') {
      errorMessage = 'Invalid phone number format.';
    } else if (error.code === 'auth/too-many-requests') {
      errorMessage =
        'Too many OTP requests. Please wait 10–15 minutes and try again, or use a different phone number.';
    } else if (error.code === 'auth/quota-exceeded') {
      errorMessage = 'Daily SMS quota exceeded. Please try again tomorrow or contact support.';
    } else if (error.code === 'auth/captcha-check-failed' || isAlreadyRenderedError(error)) {
      errorMessage = 'reCAPTCHA verification failed. Please try again.';
    } else if (error.code === 'auth/invalid-app-credential') {
      const origin =
        typeof window !== 'undefined' ? window.location.hostname : 'this domain';
      errorMessage =
        `Phone verification failed (invalid app credential) on "${origin}". ` +
        'Confirm App Check is initialized with the reCAPTCHA Enterprise site key for LNB-Web, ' +
        'this host is an authorized domain, Phone sign-in is enabled, and the project is on Blaze.';
      console.error(
        '[phoneAuth.web] auth/invalid-app-credential — checklist:\n' +
          '1. EXPO_PUBLIC_RECAPTCHA_ENTERPRISE_SITE_KEY matches App Check → LNB-Web site key\n' +
          '2. App Check is in Monitoring until web tokens succeed (enforcement blocks OTP if not)\n' +
          '3. Authentication → Settings → Authorized domains includes: ' +
          origin +
          ' (and 127.0.0.1 if you use that)\n' +
          '4. Phone sign-in enabled; project on Blaze\n' +
          '5. reCAPTCHA Enterprise key allows this domain in Google Cloud\n' +
          '6. For localhost, register an App Check debug token (EXPO_PUBLIC_APPCHECK_DEBUG_TOKEN=true)'
      );
    } else if (error.code === 'auth/operation-not-allowed') {
      errorMessage =
        'Phone authentication is not enabled for this Firebase project. Enable it in Authentication → Sign-in method.';
    } else if (error.code === 'auth/billing-not-enabled') {
      errorMessage =
        'SMS phone auth requires a Blaze (pay-as-you-go) billing plan on this Firebase project.';
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Send OTP to phone number (Web).
 * Concurrent clicks share one in-flight request so two RecaptchaVerifiers
 * cannot render into the same host at once.
 */
export const sendOTP = async (phoneNumber: string): Promise<PhoneAuthResult> => {
  if (sendOtpInFlight) {
    return sendOtpInFlight;
  }

  sendOtpInFlight = sendOTPInternal(phoneNumber).finally(() => {
    sendOtpInFlight = null;
  });

  return sendOtpInFlight;
};

/**
 * Verify OTP code (Web)
 */
export const verifyOTP = async (
  confirmation: ConfirmationResult,
  code: string
): Promise<VerifyOtpResult> => {
  try {
    if (!code || code.length !== 6) {
      return {
        success: false,
        error: 'Please enter a valid 6-digit code.',
      };
    }

    const result = await confirmation.confirm(code);

    if (!result?.user) {
      return {
        success: false,
        error: 'Authentication failed. Please try again.',
      };
    }

    return {
      success: true,
      user: result.user,
    };
  } catch (error: any) {
    console.error('Error verifying OTP:', error);

    let errorMessage = 'Invalid verification code.';

    if (error.code === 'auth/invalid-verification-code') {
      errorMessage = 'Invalid verification code. Please try again.';
    } else if (error.code === 'auth/code-expired') {
      errorMessage = 'Verification code expired. Please request a new code.';
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Sign out current user
 */
export const signOut = async (): Promise<void> => {
  try {
    await firebaseSignOut(auth);
    clearRecaptcha();
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

/**
 * Get current Firebase user
 */
export const getCurrentUser = (): User | null => {
  return auth.currentUser;
};

/**
 * Get Firebase ID token for API authentication
 */
export const getIdToken = async (forceRefresh = false): Promise<string | null> => {
  try {
    const user = getCurrentUser();
    if (!user) return null;

    return await user.getIdToken(forceRefresh);
  } catch (error) {
    console.error('Error getting ID token:', error);
    return null;
  }
};

/**
 * Listen to auth state changes
 */
export const onAuthStateChanged = (callback: (user: User | null) => void) => {
  return firebaseOnAuthStateChanged(auth, callback);
};

export const onIdTokenChanged = (callback: (user: User | null) => void) => {
  return firebaseOnIdTokenChanged(auth, callback);
};

let authReadyPromise: Promise<User | null> | null = null;

export const waitForAuthReady = (): Promise<User | null> => {
  if (authReadyPromise) {
    return authReadyPromise;
  }

  authReadyPromise = ensureAppCheckReady().then(
    () =>
      new Promise<User | null>((resolve) => {
        const unsubscribe = firebaseOnAuthStateChanged(auth, (user) => {
          unsubscribe();
          resolve(user);
        });
      })
  );

  return authReadyPromise;
};
